import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaShieldAlt, FaHeadset, FaDollarSign, FaChartLine, FaPhone, FaPhoneAlt, FaPhoneSquare, FaGlobe, FaChevronLeft, FaChevronRight, FaStar } from 'react-icons/fa';
import './Home.css';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const testimonialsPerView = 3;
  const canvasRef = useRef(null);

  // Network Animation Effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let mouseX = 0;
    let mouseY = 0;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create particles (stars)
    const particlesArray = [];
    const numberOfParticles = 80;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 1;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.opacity = Math.random() * 0.5 + 0.3;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        // Wrap around edges
        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Initialize particles
    for (let i = 0; i < numberOfParticles; i++) {
      particlesArray.push(new Particle());
    }

    // Connect particles
    const connectParticles = () => {
      const maxDistance = 150;
      for (let a = 0; a < particlesArray.length; a++) {
        for (let b = a + 1; b < particlesArray.length; b++) {
          const dx = particlesArray[a].x - particlesArray[b].x;
          const dy = particlesArray[a].y - particlesArray[b].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < maxDistance) {
            const opacity = (1 - distance / maxDistance) * 0.3;
            ctx.strokeStyle = `rgba(0, 123, 255, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particlesArray[a].x, particlesArray[a].y);
            ctx.lineTo(particlesArray[b].x, particlesArray[b].y);
            ctx.stroke();
          }
        }

        // Connect to mouse
        const dxMouse = particlesArray[a].x - mouseX;
        const dyMouse = particlesArray[a].y - mouseY;
        const distanceMouse = Math.sqrt(dxMouse * dxMouse + dyMouse * dyMouse);

        if (distanceMouse < 200) {
          const opacity = (1 - distanceMouse / 200) * 0.5;
          ctx.strokeStyle = `rgba(0, 212, 255, ${opacity})`;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(particlesArray[a].x, particlesArray[a].y);
          ctx.lineTo(mouseX, mouseY);
          ctx.stroke();
        }
      }
    };

    // Mouse move handler for the hero section
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    };

    // Mouse leave handler
    const handleMouseLeave = () => {
      mouseX = -999;
      mouseY = -999;
    };

    window.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particlesArray.forEach(particle => {
        particle.update();
        particle.draw();
      });

      connectParticles();
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  const features = [
    {
      icon: <FaShieldAlt />,
      title: "DATA SECURITY",
      description: "Your trust is our top priority. Our infrastructure is built with enterprise-grade encryption and continuous monitoring — ensuring your data stays private, protected, and available 24/7."
    },
    {
      icon: <FaHeadset />,
      title: "24/7 EXPERT ASSISTANCE",
      description: "We never clock out. Our dedicated support team is always ready to help — whether you're troubleshooting a complex integration or just need quick guidance, we've got you covered."
    },
    {
      icon: <FaDollarSign />,
      title: "BUDGET-FRIENDLY SOLUTIONS",
      description: "Smart technology shouldn't break the bank. Our scalable, cost-effective plans deliver maximum value while maintaining the premium performance your business deserves."
    },
    {
      icon: <FaChartLine />,
      title: "GROWTH-READY SOLUTIONS",
      description: "Future-proof your operations with services that grow as you do. From small teams to global enterprises, our flexible infrastructure adapts seamlessly to your evolving business needs."
    }
  ];

  const services = [
    {
      icon: <FaPhone />,
      title: "INBOUND",
      description: "Deliver exceptional customer service with our seamless inbound call management system. Instantly allocate phone numbers using our intuitive web portal, making setup and adjustments quick and hassle-free. Gain valuable insights with detailed call analytics and reporting, enabling you to monitor performance and continuously enhance your service delivery."
    },
    {
      icon: <FaPhoneAlt />,
      title: "OUTBOUND",
      description: "Maximize your outreach and boost sales efforts with our reliable and scalable outbound communication solutions. Benefit from high-quality US domestic VoIP calls that ensure clear and consistent connections, while our global A–Z SIP termination trunk routes provide extensive international reach to support your expanding business presence."
    },
    {
      icon: <FaPhoneSquare />,
      title: "DID SERVICES",
      description: "Transform the way your business communicates with a modern and flexible telephony platform designed to meet your unique needs. Our feature-rich, fully customizable Cloud PBX offers advanced functionality, including voicemail-to-email, call forwarding, and mobile app access — empowering your team to stay connected and productive from anywhere."
    },
    {
      icon: <FaGlobe />,
      title: "WHOLESALE VOIP TERMINATION",
      description: "Stay connected with clients and partners around the globe through crystal-clear voice calls offered at highly competitive rates. Our wholesale VoIP termination services deliver seamless, high-quality communication, ensuring reliability without compromising performance or cost-efficiency."
    }
  ];

  const testimonials = [
    { name: "Sarah Johnson", company: "TechStart Inc.", role: "CEO", rating: 5, text: "DirectRoute Networks transformed our communication infrastructure. The call quality is exceptional and the support team is always there when we need them." },
    { name: "Michael Chen", company: "Global Logistics Ltd", role: "Operations Manager", rating: 5, text: "Switching to DirectRoute's VoIP solution cut our communication costs by 40% while improving call clarity. Best decision we've made!" },
    { name: "Emily Rodriguez", company: "CustomerFirst Solutions", role: "Call Center Director", rating: 5, text: "The inbound call management system is incredibly intuitive. Our team was up and running in less than a day with zero downtime." },
    { name: "David Thompson", company: "SalesForce Pro", role: "VP of Sales", rating: 5, text: "Our outbound campaigns have never been more effective. The global reach and crystal-clear connections have boosted our conversion rates significantly." },
    { name: "Lisa Wang", company: "Enterprise Connect", role: "IT Director", rating: 5, text: "The Cloud PBX features are outstanding. Voicemail-to-email and mobile integration have made our remote team incredibly productive." },
    { name: "James Miller", company: "International Trade Co.", role: "Managing Director", rating: 5, text: "We operate in 15 countries and DirectRoute's wholesale VoIP termination keeps us connected seamlessly. Reliability is unmatched!" },
    { name: "Amanda Foster", company: "HealthCare Plus", role: "Administrator", rating: 5, text: "Data security was our top concern, and DirectRoute exceeded all expectations. Enterprise-grade encryption gives us complete peace of mind." },
    { name: "Robert Kim", company: "StartUp Ventures", role: "Founder", rating: 5, text: "As a growing startup, we needed budget-friendly solutions that didn't compromise quality. DirectRoute delivered exactly that!" },
    { name: "Jennifer Martinez", company: "Retail Solutions Group", role: "COO", rating: 5, text: "The 24/7 support is genuinely available. We had an integration question at 2 AM and got immediate expert assistance." },
    { name: "Christopher Lee", company: "Financial Services Inc.", role: "CTO", rating: 5, text: "Scalability was crucial for us. DirectRoute's infrastructure grew with us from 10 to 500 employees without a hitch." },
    { name: "Michelle Brown", company: "Marketing Dynamics", role: "Director", rating: 5, text: "The analytics and reporting features give us insights we never had before. We can now optimize our call strategies in real-time." },
    { name: "Daniel Garcia", company: "Tech Innovations", role: "VP Engineering", rating: 5, text: "Integration with our existing systems was seamless. The API documentation is excellent and support helped us every step of the way." },
    { name: "Rachel Adams", company: "Customer Care Pro", role: "Service Manager", rating: 5, text: "Call quality is consistently excellent, even during peak hours. Our customer satisfaction scores have increased by 25%." },
    { name: "Kevin Patel", company: "E-Commerce Solutions", role: "CEO", rating: 5, text: "The DID services transformed how we handle customer inquiries. Multiple departments can now manage calls efficiently." },
    { name: "Sophia Turner", company: "Global Consulting", role: "Partner", rating: 5, text: "We serve clients worldwide and DirectRoute's A-Z termination ensures we're always connected, no matter the destination." },
    { name: "Mark Wilson", company: "Insurance Brokers Ltd", role: "Operations Head", rating: 5, text: "The platform's reliability is incredible. We've had 99.9% uptime since switching to DirectRoute Networks." },
    { name: "Nicole Anderson", company: "Real Estate Connect", role: "Broker", rating: 5, text: "Mobile app access means I never miss an important client call, even when I'm showing properties. Game changer!" },
    { name: "Brian Taylor", company: "Manufacturing Corp", role: "Plant Manager", rating: 5, text: "Setting up phone numbers through the web portal is so easy. We can adjust our communication setup on the fly." },
    { name: "Angela White", company: "Legal Services Group", role: "Managing Partner", rating: 5, text: "Security and privacy are paramount in our industry. DirectRoute's encryption and monitoring give us complete confidence." },
    { name: "Thomas Jackson", company: "Hospitality International", role: "GM", rating: 5, text: "Managing multiple hotel locations is effortless with DirectRoute's centralized system. Guests always reach the right department." },
    { name: "Olivia Martinez", company: "Education Services", role: "Dean", rating: 5, text: "The cost savings have allowed us to invest more in student services. Quality hasn't suffered one bit!" },
    { name: "William Davis", company: "Transportation Hub", role: "Logistics Coordinator", rating: 5, text: "Real-time call routing has improved our dispatch efficiency by 35%. Our drivers and customers are happier than ever." },
    { name: "Jessica Moore", company: "Media Productions", role: "Producer", rating: 5, text: "Crystal-clear audio is essential for our business. DirectRoute delivers broadcast-quality voice communication every time." },
    { name: "Ryan Thompson", company: "Fitness Network", role: "Regional Manager", rating: 5, text: "Rolling out VoIP across 50 gym locations was smooth and fast. The support team made it look easy!" },
    { name: "Lauren Harris", company: "Professional Services", role: "Partner", rating: 5, text: "From implementation to daily operations, DirectRoute Networks has been the perfect communication partner for our firm." }
  ];

  const totalSlides = Math.ceil(testimonials.length / testimonialsPerView);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  return (
    <div className="home-container">
      <section className="home-hero">
        <canvas ref={canvasRef} className="hero-canvas"></canvas>
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="home-hero-eyebrow">WELCOME TO DirectRoute Networks</p>
            <h1 className="home-hero-title">DirectRoute Networks</h1>
            <p className="home-hero-subtitle">
              DirectRoute Networks, your gateway to stellar global VoIP and communication services. As a leading provider in the industry, Dial Edge Telecom boasts a vast network of global telecom alliances and a blueprint infrastructure, positioning us as the most reliable choice for VoIP and SMS service connectivity.
            </p>
            <Link to="/contact" className="home-btn">Get Started Today</Link>
          </motion.div>
        </div>
      </section>

      <section className="home-features">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="home-section-title">
              Why Choose DirectRoutes?
            </h2>
            <div className="home-features-grid">
              {features.map((feature, index) => (
                <motion.div
                  className="feature-card"
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  {feature.icon}
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="home-services">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="home-section-title">COMPREHENSIVE CALL CENTER SOLUTIONS</h2>
            <p className="home-services-intro">
              Empower your business with end-to-end call center services designed to enhance customer engagement, streamline operations, and boost overall efficiency.
            </p>
            <div className="home-services-grid">
              {services.map((service, index) => (
                <motion.div
                  className="service-card"
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  {service.icon}
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="home-stats">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="home-stats-content">
              <div className="home-stats-text">
                <h2 className="home-stats-title">Our Stats</h2>
                <p className="home-stats-description">We help you to unleash the power within your business</p>
              </div>
              <div className="home-stats-grid">
                <motion.div
                  className="stat-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="stat-number">100+</div>
                  <div className="stat-label">Projects Completed</div>
                </motion.div>
                <motion.div
                  className="stat-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="stat-number">250%</div>
                  <div className="stat-label">Company Growth</div>
                </motion.div>
                <motion.div
                  className="stat-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <div className="stat-number">3+</div>
                  <div className="stat-label">Years of Experience</div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

{/*	<section className="home-testimonials">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="home-section-title">WHAT OUR CLIENTS SAY</h2>
            <p className="home-testimonials-intro">
              Trusted by businesses worldwide. Here's what our clients have to say about their experience with DirectRoute Networks.
            </p>
            
            <div className="testimonials-carousel">
              <button className="carousel-btn prev-btn" onClick={prevSlide} aria-label="Previous testimonials">
                <FaChevronLeft />
              </button>
              
              <div className="testimonials-wrapper">
                <div 
                  className="testimonials-track"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                    <div key={slideIndex} className="testimonials-slide">
                      {testimonials
                        .slice(slideIndex * testimonialsPerView, (slideIndex + 1) * testimonialsPerView)
                        .map((testimonial, index) => (
                          <motion.div
                            key={slideIndex * testimonialsPerView + index}
                            className="testimonial-card"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            viewport={{ once: true }}
                          >
                            <div className="testimonial-rating">
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <FaStar key={i} />
                              ))}
                            </div>
                            <p className="testimonial-text">"{testimonial.text}"</p>
                            <div className="testimonial-author">
                              <h4>{testimonial.name}</h4>
                              <p>{testimonial.role}</p>
                              <p className="testimonial-company">{testimonial.company}</p>
                            </div>
                          </motion.div>
                        ))}
                    </div>
                  ))}
                </div>
              </div>
              
              <button className="carousel-btn next-btn" onClick={nextSlide} aria-label="Next testimonials">
                <FaChevronRight />
              </button>
            </div>

            <div className="carousel-dots">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  className={`dot ${currentSlide === index ? 'active' : ''}`}
                  onClick={() => setCurrentSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>
*/}
      <section className="home-cta">
        <div className="home-cta-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Transform Your Network?</h2>
            <p>
              Join thousands of professionals who have already discovered the power of DirectRoutes Network.
            </p>
            <Link to="/contact" className="home-btn">Start Your Journey</Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;



